import { useMemo, useState } from "react";
import { ChevronRight } from "lucide-react";

import { cn, displayPrice } from "@/lib/utils";
import { TopSellingItem } from "@/redux/services/pos/pos.type";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { computeDelta, Delta, DeltaText } from "./reportDelta";
import { reportCard } from "./reportCard";

type SortKey = "quantity" | "revenue";

const DISPLAY_LIMIT = 10;
const COLLAPSED_COUNT = 5;

function readInitialSort(storageKey: string): SortKey {
  try {
    return localStorage.getItem(storageKey) === "revenue"
      ? "revenue"
      : "quantity";
  } catch {
    return "quantity";
  }
}

function persistSort(storageKey: string, sort: SortKey): void {
  try {
    localStorage.setItem(storageKey, sort);
  } catch {
    // Ignore storage failures (private mode / quota); the toggle still works
    // for the current session.
  }
}

interface Row {
  item: TopSellingItem;
  share: number;
  delta: Delta;
}

interface TopSellingItemsSectionProps {
  title: string;
  items: TopSellingItem[];
  previousItems?: TopSellingItem[];
  currency: string;
  /** "top" ranks highest-first; "least" ranks lowest-first. */
  order?: "top" | "least";
  /** localStorage key for the sort preference (kept separate per card). */
  storageKey: string;
}

function TopSellingItemsSection({
  title,
  // Default to [] so a partial/rolled-back API that omits the field can't
  // crash the Rapports page (SFRESTO-98).
  items = [],
  previousItems,
  currency,
  order = "top",
  storageKey,
}: TopSellingItemsSectionProps) {
  const [sort, setSort] = useState<SortKey>(() => readInitialSort(storageKey));
  const [showModal, setShowModal] = useState(false);

  const previousByName = useMemo(() => {
    const map = new Map<string, TopSellingItem>();
    (previousItems ?? []).forEach((item) => map.set(item.name, item));
    return map;
  }, [previousItems]);

  const rows = useMemo<Row[]>(() => {
    const primary = sort;
    const secondary = sort === "quantity" ? "revenue" : "quantity";
    const sorted = [...items]
      .sort((a, b) =>
        order === "least"
          ? a[primary] - b[primary] ||
            a[secondary] - b[secondary] ||
            a.name.localeCompare(b.name)
          : b[primary] - a[primary] ||
            b[secondary] - a[secondary] ||
            a.name.localeCompare(b.name),
      )
      .slice(0, DISPLAY_LIMIT);

    const total = sorted.reduce((sum, item) => sum + item[sort], 0);

    return sorted.map((item) => ({
      item,
      share: total > 0 ? (item[sort] / total) * 100 : 0,
      delta: computeDelta(item[sort], previousByName.get(item.name)?.[sort]),
    }));
  }, [items, sort, order, previousByName]);

  if (items.length === 0) {
    return null;
  }

  const changeSort = (next: SortKey) => {
    setSort(next);
    persistSort(storageKey, next);
  };

  const hiddenCount = rows.length - COLLAPSED_COUNT;

  return (
    <section className={cn(reportCard, "p-6")}>
      <div className="mb-4 flex items-center justify-between gap-3">
        <h2 className="text-sm font-medium uppercase tracking-wide text-muted-foreground">
          {title}
        </h2>
        <SortToggle sort={sort} onChange={changeSort} />
      </div>

      <div className="space-y-2">
        {rows.slice(0, COLLAPSED_COUNT).map((row, index) => (
          <TopSellingRow
            key={`${row.item.name}-${index}`}
            row={row}
            index={index}
            currency={currency}
          />
        ))}
      </div>

      {hiddenCount > 0 && (
        <button
          type="button"
          onClick={() => setShowModal(true)}
          className="mt-3 flex w-full items-center justify-between rounded-lg px-3 py-2 text-sm font-medium text-primary transition-colors hover:bg-muted"
        >
          <span>Voir plus ({hiddenCount} de plus)</span>
          <ChevronRight className="h-4 w-4" />
        </button>
      )}

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center justify-between gap-3 pr-6">
              <DialogTitle>{title}</DialogTitle>
              <SortToggle sort={sort} onChange={changeSort} />
            </div>
          </DialogHeader>
          <div className="mt-4 space-y-2">
            {rows.map((row, index) => (
              <TopSellingRow
                key={`${row.item.name}-${index}`}
                row={row}
                index={index}
                currency={currency}
              />
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
}

function SortToggle({
  sort,
  onChange,
}: {
  sort: SortKey;
  onChange: (next: SortKey) => void;
}) {
  return (
    <div
      role="group"
      aria-label="Trier"
      className="flex shrink-0 rounded-lg border border-border p-0.5"
    >
      {(
        [
          { key: "quantity", label: "Quantité" },
          { key: "revenue", label: "Revenu" },
        ] as const
      ).map(({ key, label }) => (
        <button
          key={key}
          type="button"
          aria-pressed={sort === key}
          onClick={() => onChange(key)}
          className={cn(
            "rounded-md px-2.5 py-1 text-xs font-medium transition-colors",
            sort === key
              ? "bg-primary text-primary-foreground"
              : "text-muted-foreground hover:text-foreground",
          )}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

function TopSellingRow({
  row,
  index,
  currency,
}: {
  row: Row;
  index: number;
  currency: string;
}) {
  const isEven = index % 2 === 0;
  return (
    <div
      className={cn(
        "flex items-center justify-between gap-3 rounded-lg p-3 transition-shadow hover:shadow-sm",
        isEven ? "bg-gray-50" : "bg-white",
      )}
    >
      <div className="flex min-w-0 items-center gap-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary text-sm font-bold text-primary-foreground">
          {index + 1}
        </div>
        <div className="min-w-0">
          <p className="truncate font-medium text-card-foreground">
            {row.item.name}
          </p>
          <p className="truncate text-sm text-muted-foreground">
            {row.item.quantity} vendus · {displayPrice(row.item.revenue, currency)}
          </p>
        </div>
      </div>
      <div className="shrink-0 text-right">
        <p className="font-semibold tabular-nums text-card-foreground">
          {row.share.toFixed(1)}%
        </p>
        <p className="text-xs text-muted-foreground">des ventes</p>
        <div className="flex justify-end">
          <DeltaText delta={row.delta} className="text-xs" />
        </div>
      </div>
    </div>
  );
}

export default TopSellingItemsSection;
